import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  IAIProviderCatalogEntry,
  IConnectionTestResult,
  IWorkspaceAIProvider,
  IWorkspaceAIProviderWrite,
} from "@/types/workspace-ai-provider";

/**
 * The workspace AI-provider endpoints (WS1/WS2 of NG-1357).
 *
 * `save` and `testConnection` make real calls to the chosen provider on the
 * backend and can take several seconds; callers show progress (the
 * response-time budget for a watched compute is 2-10s with an indicator).
 */
export class WorkspaceAIProviderService extends ApiService {
  async getSetting(slug: string, config?: ApiRequestConfig): Promise<IWorkspaceAIProvider> {
    return this.get(`/api/workspaces/${slug}/ai-provider/`, config);
  }

  async getCatalog(slug: string, config?: ApiRequestConfig): Promise<IAIProviderCatalogEntry[]> {
    return this.get(`/api/workspaces/${slug}/ai-provider/catalog/`, config);
  }

  /** Runs the live connection test without saving; a failed probe is a 200 with `ok: false`. */
  async testConnection(
    slug: string,
    body: IWorkspaceAIProviderWrite,
  ): Promise<IConnectionTestResult> {
    return this.post(`/api/workspaces/${slug}/ai-provider/test/`, body);
  }

  /** Save = verify: the backend runs the test first and answers 422 with the
   *  result when a probe fails, 409 when the stored credential changed under us. */
  async save(slug: string, body: IWorkspaceAIProviderWrite): Promise<IWorkspaceAIProvider> {
    return this.put(`/api/workspaces/${slug}/ai-provider/`, body);
  }

  /** Back to Cognota-managed: deletes the workspace's row. */
  async reset(slug: string): Promise<void> {
    await this.delete(`/api/workspaces/${slug}/ai-provider/`);
  }
}
